import * as XLSX from "xlsx";

import {
  IGenericUploadParseResult,
  IGenericUploadRowInfo,
  TGenericUploadCsvRow,
} from "@ryde/shared";

const toCellString = (cell: unknown): string =>
  cell === null || cell === undefined ? "" : String(cell);

const isEmptyRow = (row: unknown[]): boolean =>
  row.every(cell => toCellString(cell).trim() === "");

/**
 * Parses an Excel file (.xlsx / .xls) into the same row shape produced by the
 * CSV parser: the first row is treated as the header, every following non-empty
 * row becomes a record keyed by header, and any cells beyond the header columns
 * are collected into `extraFields` (mirroring papaparse's `__parsed_extra`).
 * Only the first worksheet is read.
 */
export const genericUploadExcelFile = async ({
  file,
}: {
  file: File;
}): Promise<IGenericUploadParseResult> => {
  const result: IGenericUploadParseResult = {
    data: [],
  };

  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: "array" });

  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) {
    return result;
  }

  const worksheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json<string[]>(worksheet, {
    header: 1,
    raw: false,
    defval: "",
    blankrows: false,
  });

  const [headers, ...dataRows] = rows;

  if (!headers) {
    return result;
  }

  let rowIndex = 0;

  for (const rawRow of dataRows) {
    if (isEmptyRow(rawRow)) {
      continue;
    }

    rowIndex += 1;

    const row: TGenericUploadCsvRow = {};
    const extraFields: string[] = [];

    rawRow.forEach((cell, cellIndex) => {
      const value = String(cell);
      const header = headers[cellIndex];

      if (header) {
        row[header] = value;
      } else {
        extraFields.push(value);
      }
    });

    const transformedItem: IGenericUploadRowInfo = {
      index: rowIndex,
      row,
    };

    if (extraFields.length) {
      transformedItem.extraFields = extraFields;
    }

    result.data.push(transformedItem);
  }

  return result;
};
